import React, { useState } from "react";
import { Outlet, NavLink, Navigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import {
  Shield, LayoutDashboard, Building2, Ticket, Activity, Megaphone, LogOut,
  CheckSquare, Users, KeyRound, ShieldCheck, Trash2, BarChart3, FileText,
} from "lucide-react";
import ForcePasswordReset from "@/pages/admin/ForcePasswordReset";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ThemeToggle from "@/components/ThemeToggle";
import { Toaster } from "sonner";

const NAV = [
  { to: "/admin/dashboard",       icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/entities",        icon: Building2,       label: "Entities" },
  { to: "/admin/usage",           icon: BarChart3,       label: "Usage metrics" },
  { to: "/admin/approvals",       icon: CheckSquare,     label: "Approvals" },
  { to: "/admin/team",            icon: Users,           label: "Admin team" },
  { to: "/admin/tickets",         icon: Ticket,          label: "Tickets" },
  { to: "/admin/security",        icon: Activity,        label: "Admin actions" },
  { to: "/admin/auth-events",     icon: ShieldCheck,     label: "Auth log" },
  { to: "/admin/password-policy", icon: KeyRound,        label: "Password policy" },
  { to: "/admin/broadcasts",      icon: Megaphone,       label: "Broadcasts" },
  { to: "/admin/brochure",        icon: FileText,        label: "Brochure designer" },
  { to: "/admin/data-wipe",       icon: Trash2,          label: "Data wipe" },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAdmin();
  const [openPw, setOpenPw] = useState(false);
  if (loading) return <div className="p-8 text-foreground bg-background min-h-screen">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {admin.must_reset_password && <ForcePasswordReset />}
      <aside className="w-60 border-r border-border bg-card text-card-foreground flex flex-col" data-testid="admin-sidebar">
        <div className="px-4 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <img src="/zenith360-logo.png" alt="Zenith 360" className="h-9 w-auto" />
            <div>
              <div className="font-heading font-bold text-sm leading-tight text-foreground">Zenith360-ERP</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Admin console</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-sm text-[13px] transition-colors ${isActive ? "bg-orange-500/10 text-foreground border-l-2 border-orange-500 pl-[10px] font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <n.icon className="w-4 h-4 shrink-0" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3 text-[11px]">
          <div className="font-medium truncate text-foreground">{admin.full_name || admin.email}</div>
          <div className="text-muted-foreground truncate">{admin.email}</div>
          <div className="mt-1 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-orange-500/15 text-orange-600 dark:text-orange-400">Zenith360-ERP Admin</div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <button onClick={() => setOpenPw(true)} data-testid="admin-change-password"
                className="text-muted-foreground hover:text-foreground flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> Change password
              </button>
              <button onClick={logout} data-testid="admin-logout" className="text-muted-foreground hover:text-foreground flex items-center gap-1.5">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
            <ThemeToggle variant="compact" />
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto bg-background text-foreground"><Outlet /></main>
      <ChangePasswordDialog open={openPw} onOpenChange={setOpenPw} endpoint="/admin/change-password" />
      <Toaster theme="light" position="top-right" richColors />
    </div>
  );
}
